<template>
  <div class="app-breadcrumbs">
    <div class="items">

      <!-- Menu -->
      <div v-if="isAdmin" class="btn-menu" @click.stop="$emit('toggle-menu')">
        <span class="i-header-menu"></span>
      </div>

      <!-- Breadcrumb Items -->
      <div v-for="(item, index) in breadcrumb" class="item" :key="index">
        <div class="item-btn" :class="{ 'item-btn__active': isActive(index) }" @click="onItemClick(item, index)">
          {{ item.title | translate }}
        </div>

        <!-- Arrow separator -->
        <div v-if="index < breadcrumb.length - 1" class="splitter">
          <span class="i-header-breadcrumb-arrow"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import { getFarmPath } from '@/util/app.js'

export default {
  name: 'AppBreadcrumbs',

  data: () => ({
    prevFarmId: null,
    farmName: null,
    breadcrumb: [],
    lastMixerId: null,
    lastModelId: null
  }),

  computed: {
    ...mapGetters('baseMixers', ['mixerName', 'modelName']),

    isAdmin () {
      return this.$store.getters['account/isAdmin']
    },

    isStaff () {
      return this.$store.getters['account/isStaff']
    },

    isClient () {
      return this.$store.getters['account/isClient']
    }
  },

  mounted () {
    this.buildBreadcrumbs()
  },

  methods: {
    ...mapActions('farms', ['getFarm']),

    isActive (index) {
      if (!this.isClient && this.$route.path.includes('consultants')) {
        return false
      }
      return !this.$route.path.includes('admin') &&
        index === this.breadcrumb.length - 1
    },

    async getCountryName (id) {
      if (id === 'null') return 'Без групи'

      const { data } = await api.dashboard.groups.getById(id)
      return data.name
    },

    async getCompanyName (id) {
      const KEY = `company_${id}_name`
      const restored = sessionStorage.getItem(KEY)
      if (restored) {
        return restored
      }
      const { data, errors } = await api.companies.getById(id)
      if (!errors) {
        sessionStorage.setItem(KEY, data.name)
        return data.name
      }
      return 'Company'
    },

    async getFarmName (id) {
      const KEY = `farm_${id}_name`
      const restored = sessionStorage.getItem(KEY)
      if (restored) {
        return restored
      }
      const { data, errors } = await api.farms.getById(id)
      if (!errors) {
        sessionStorage.setItem(KEY, data.name)
        return data.name
      }
      return 'Farm'
    },

    getClientName (id) {
      const clients = JSON.parse(localStorage.getItem('activities_clients') || '{}')
      return clients[id] || `Client ${id}`
    },

    async getMixerName (id) {
      try {
        const { data, errors } = await api.dashboard.mixers.getById(id)
        if (!errors && data) {
          return data.name
        }
      } catch (e) {
        console.error('Error loading mixer name:', e)
      }

      return `Mixer ${id}`
    },

    async getModelName (id) {
      try {
        const { data, errors } = await api.dashboard.mixers.getModelById(id)
        if (!errors && data) {
          return data.name
        }
      } catch (e) {
        console.error('Error loading model name:', e)
      }

      return `Model ${id}`
    },

    async buildBreadcrumbs () {
      const breadcrumbTmp = []

      // Breadcrumbs for consultants
      if (this.$route.path.startsWith('/app/consultants')) {
        breadcrumbTmp.push({
          title: 'base.consultants',
          path: '/app/consultants'
        })

        this.breadcrumb = breadcrumbTmp
        return
      }

      // Breadcrumbs for admin sections
      if (this.$route.path.includes('/admin/')) {
        // Check if it's client-specific admin (e.g., /app/clients/1143/admin/users)
        const clientIdMatch = this.$route.path.match(/\/clients\/(\d+)\/admin/)
        if (clientIdMatch) {
          const clientId = clientIdMatch[1]
          if (this.isAdmin || this.isStaff) {
            breadcrumbTmp.push({
              title: 'activities.countries',
              path: '/app/activities'
            })

            // Get client name from localStorage
            const clientName = this.getClientName(clientId)
            breadcrumbTmp.push({
              title: clientName,
              path: `/app/clients/${clientId}`
            })
          } else {
            breadcrumbTmp.push({
              title: 'base.companies',
              path: '/app/companies'
            })
          }

          this.breadcrumb = breadcrumbTmp
          return
        }

        if (this.$route.path.startsWith('/app/admin/')) {
          if (this.isAdmin || this.isStaff) {
            breadcrumbTmp.push({
              title: 'activities.countries',
              path: '/app/activities'
            })
          } else {
            breadcrumbTmp.push({
              title: 'base.companies',
              path: '/app/companies'
            })
          }

          this.breadcrumb = breadcrumbTmp
          return
        }
      }

      // Breadcrumbs for mixer-db
      if (this.$route.path.startsWith('/app/mixer-db')) {
        breadcrumbTmp.push({
          title: 'База Міксерів',
          path: '/app/mixer-db'
        })

        const { mixerId, modelId } = this.$route.params

        if (mixerId) {
          if (this.lastMixerId !== mixerId) {
            this.lastMixerId = mixerId
            this.$store.dispatch('baseMixers/resetState')
          }

          const title = this.mixerName || await this.getMixerName(mixerId)
          breadcrumbTmp.push({
            title,
            path: `/app/mixer-db/${mixerId}/models`
          })
        }
        if (modelId) {
          if (this.lastModelId !== modelId) {
            this.lastModelId = modelId
          }
          const title = this.modelName || await this.getModelName(modelId)
          breadcrumbTmp.push({
            title,
            path: `/app/mixer-db/${mixerId}/models/${modelId}/calibrations`
          })
        }

        this.breadcrumb = breadcrumbTmp
        return
      }

      if (this.$route.path.startsWith('/app/activities')) {
        breadcrumbTmp.push({
          title: 'activities.countries',
          path: '/app/activities'
        })

        const acClientId = this.$route.params.ac_client_id
        if (acClientId) {
          breadcrumbTmp.push({
            title: await this.getCountryName(acClientId),
            path: `/app/activities/${acClientId}`
          })
        }

        const clientId = this.$route.params.client_id
        if (clientId && acClientId) {
          const clients = JSON.parse(localStorage.getItem('activities_clients') || '{}')
          const clientName = clients[clientId] || 'base.companies'

          breadcrumbTmp.push({
            title: clientName,
            path: `/app/activities/${acClientId}/clients/${clientId}`
          })
        }


        const companyId = this.$route.params.company_id
        if (companyId && clientId && acClientId) {
          const farmId = this.$route.params.farm_id
          breadcrumbTmp.push({
            title: await this.getCompanyName(companyId),
            path: farmId
              ? `/app/activities/${acClientId}/clients/${clientId}/companies/${companyId}/farms`
              : `/app/activities/${acClientId}/clients/${clientId}/companies/${companyId}`
          })
        }

        const farmId = this.$route.params.farm_id
        if (farmId && companyId && clientId && acClientId) {
          breadcrumbTmp.push({
            title: await this.getFarmName(farmId),
            path: `/app/activities/${acClientId}/clients/${clientId}/companies/${companyId}/farms/${farmId}`
          })
        }

        this.breadcrumb = breadcrumbTmp
        return
      }

      this.lastMixerId = null
      this.lastModelId = null

      // Default breadcrumbs logic
      if (!this.isClient) {
        breadcrumbTmp.push({
          title: 'base.clients',
          path: '/app/clients'
        })
      }

      if (this.$route.path.startsWith('/app')) {
        const clientId = this.$route.params.client_id || this.$store.getters.clientId
        breadcrumbTmp.push({
          title: 'base.companies',
          path: this.isClient
            ? '/app/companies'
            : `/app/clients/${clientId}/companies`
        })
      }

      const companyId = +this.$route.params.company_id
      if (companyId) {
        const clientId = this.$route.params.client_id || this.$store.getters.clientId
        const farmId = this.$route.params.farm_id
        breadcrumbTmp.push({
          title: await this.getCompanyName(companyId),
          path: this.isClient
            ? `/app/companies/${companyId}`
            : farmId
              ? `/app/clients/${clientId}/companies/${companyId}/farms`
              : `/app/clients/${clientId}/companies/${companyId}`
        })
      }

      const farmId = this.$route.params.farm_id
      if (farmId) {
        breadcrumbTmp.push({
          title: await this.getFarmName(farmId),
          path: getFarmPath(null, this.$route, 'planning')
        })
      }

      this.breadcrumb = breadcrumbTmp
    },

    onItemClick (item, index) {
      // if (item.path === '/app/activities') {
      //   this.$store.dispatch('setClientId', null)
      // }
      if (!this.isActive(index)) {
        this.$router.push(item.path)
      }
    }
  },

  watch: {
    '$route.path' () {
      this.buildBreadcrumbs()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.app-breadcrumbs {
  height: 7rem;
  margin-left: 14rem;
  width: 100%;
  padding-right: 30rem;

  @media screen and (max-width: 1600px) {
    margin-left: 7rem;
  }

  @media screen and (max-width: 1400px) {
    margin-left: 3rem;
  }

  .items {
    display: flex;
     @media screen and (max-width: 1200px) {
    .item {
      &:last-child {
        display: none;
      }
    }
  }

    .item {
      display: flex;
      align-items: baseline;
    }
  }
}

.btn-menu {
  cursor: pointer;
  display: flex;
  align-items: center;
  padding-top: .5rem;
}

.item-btn {
  cursor: pointer;
  list-style: none;
  display: inline-block;
  font-size: 1.6rem;
  font-weight: 700;
  padding-top: 29px;
  padding-bottom: 19px;
  margin-right: 30px;
  margin-left: 30px;
  border-bottom: 4px solid transparent;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media screen and (max-width: 1900px) {
    margin-right: 12px;
    margin-left: 12px;
  }

  @media screen and (max-width: 1800px) {
    font-size: 1.5rem;
    max-width: 180px;
  }

  @media screen and (max-width: 1350px) {
    font-size: 1.3rem;
    max-width: 150px;
  }
  @media screen and (max-width: 1200px) {
    max-width: 100px;
  }



  &__active {
    cursor: default;
    border-bottom: 4px solid var(--color-primary);
  }
}
</style>